import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../components/shared/cp-page-subnav.js';

const SYSTEM_TABS = [
  { label: 'Settings', path: '/settings' },
  { label: 'Agenten', path: '/agents' },
  { label: 'Team & Skills', path: '/team' },
  { label: 'Agent-Skills', path: '/skills' },
  { label: 'Routinen', path: '/cron' },
  { label: 'Monitoring', path: '/monitoring' },
];

interface BudgetUsage {
  windowStart: number;
  count: number;
  cap: number;
  remaining: number;
}

interface BudgetHistoryEntry {
  windowStart: number;
  spawnCount: number;
}

interface RoutineStatus {
  id: number;
  name: string;
  enabled: number;
  provider: string | null;
  model: string | null;
  last_run: string | null;
  last_exec_status: string | null;
  last_exec_started_at: string | null;
  last_exec_completed_at: string | null;
}

interface PausedStrategy {
  id: number;
  goal_title: string;
  agent_name: string;
  company_name: string;
  block_reason: string | null;
  updated_at: string;
}

interface RunningSpawn {
  id: number;
  cron_job_id: number;
  started_at: string;
  status: string;
}

interface Overview {
  hourlyBudget: BudgetUsage;
  routines: RoutineStatus[];
  pausedStrategies: PausedStrategy[];
  runningSpawns: RunningSpawn[];
  skippedLastHour: number;
}

@customElement('cp-page-monitoring')
export class CpPageMonitoring extends LitElement {
  createRenderRoot() { return this; }

  @state() private _overview: Overview | null = null;
  @state() private _budgetHistory: BudgetHistoryEntry[] = [];
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _resuming = new Set<number>();

  private _pollTimer: ReturnType<typeof setInterval> | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this._load();
    this._pollTimer = setInterval(() => void this._load(), 10_000);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._pollTimer) clearInterval(this._pollTimer);
  }

  private async _load(): Promise<void> {
    try {
      const [overview, history] = await Promise.all([
        fetch('/api/monitoring/overview').then(r => r.json()),
        fetch('/api/monitoring/budget-history').then(r => r.json()),
      ]);
      this._overview = overview as Overview;
      this._budgetHistory = history as BudgetHistoryEntry[];
      this._error = '';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private async _resumeStrategy(id: number): Promise<void> {
    this._resuming = new Set([...this._resuming, id]);
    try {
      await fetch(`/api/monitoring/strategies/${id}/resume`, { method: 'POST' });
      await this._load();
    } finally {
      this._resuming = new Set([...this._resuming].filter(x => x !== id));
    }
  }

  private _formatTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return 'gerade eben';
    if (diff < 3_600_000) return `vor ${Math.floor(diff / 60_000)}min`;
    if (diff < 86_400_000) return `vor ${Math.floor(diff / 3_600_000)}h`;
    return d.toLocaleDateString('de-DE');
  }

  private _statusBadge(status: string | null) {
    if (!status) return html`<span class="cp-monitoring__badge cp-monitoring__badge--none">—</span>`;
    const cls = {
      success: 'cp-monitoring__badge--success',
      running: 'cp-monitoring__badge--running',
      failed: 'cp-monitoring__badge--failed',
      timeout: 'cp-monitoring__badge--timeout',
    }[status] ?? 'cp-monitoring__badge--none';
    return html`<span class="cp-monitoring__badge ${cls}">${status}</span>`;
  }

  private _renderBudgetBar(budget: BudgetUsage) {
    const pct = Math.min(100, Math.round((budget.count / budget.cap) * 100));
    const barCls = pct >= 90 ? 'danger' : pct >= 70 ? 'warn' : 'ok';
    const resetMs = 3_600_000 - (Date.now() - budget.windowStart);
    const resetMin = Math.max(0, Math.floor(resetMs / 60_000));
    return html`
      <div class="cp-monitoring__budget-section">
        <div class="cp-monitoring__budget-header">
          <span class="cp-monitoring__budget-label">Spawn-Budget (Stunde)</span>
          <span class="cp-monitoring__budget-count">${budget.count} / ${budget.cap}</span>
          <span class="cp-monitoring__budget-reset">Reset in ${resetMin}min</span>
        </div>
        <div class="cp-monitoring__budget-bar-track">
          <div class="cp-monitoring__budget-bar cp-monitoring__budget-bar--${barCls}" style="width:${pct}%"></div>
        </div>
        ${budget.remaining === 0 ? html`<p class="cp-monitoring__budget-exhausted">Budget erschoepft — Spawns gesperrt bis naechste Stunde</p>` : nothing}
      </div>
    `;
  }

  private _renderSparkline(history: BudgetHistoryEntry[]) {
    if (history.length === 0) return nothing;
    const max = Math.max(...history.map(h => h.spawnCount), 1);
    const bars = history.map(h => {
      const pct = Math.round((h.spawnCount / max) * 100);
      const t = new Date(h.windowStart).getHours();
      return html`<div class="cp-monitoring__spark-bar" style="height:${pct}%" title="${t}:00 — ${h.spawnCount} Spawns"></div>`;
    });
    return html`
      <div class="cp-monitoring__sparkline-section">
        <p class="cp-monitoring__section-label">Spawns letzte 24h</p>
        <div class="cp-monitoring__sparkline">${bars}</div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-page">
        <cp-page-subnav .tabs=${SYSTEM_TABS} activePath="/monitoring"></cp-page-subnav>

        <div class="cp-page__content">
          <div class="cp-page__header">
            <h1 class="cp-page__title">System Monitoring</h1>
          </div>

          ${this._loading ? html`<div class="cp-monitoring__loading">Laden...</div>` : nothing}
          ${this._error ? html`<div class="cp-monitoring__error">${this._error}</div>` : nothing}

          ${this._overview ? this._renderContent(this._overview) : nothing}
        </div>
      </div>
    `;
  }

  private _renderContent(ov: Overview) {
    return html`
      <div class="cp-monitoring__stats-row">
        ${this._renderBudgetBar(ov.hourlyBudget)}
        ${this._renderSparkline(this._budgetHistory)}
      </div>

      ${ov.runningSpawns.length > 0 ? html`
        <div class="cp-monitoring__section">
          <p class="cp-monitoring__section-label">Laufend (${ov.runningSpawns.length})</p>
          <div class="cp-monitoring__running-list">
            ${ov.runningSpawns.map(s => html`
              <div class="cp-monitoring__running-item">
                <span class="cp-monitoring__badge cp-monitoring__badge--running">running</span>
                <span>Job #${s.cron_job_id}</span>
                <span class="cp-monitoring__time">${this._formatTime(s.started_at)}</span>
              </div>
            `)}
          </div>
        </div>
      ` : nothing}

      ${ov.skippedLastHour > 0 ? html`
        <div class="cp-monitoring__skip-note">
          ${ov.skippedLastHour} Spawn(s) in letzter Stunde uebersprungen (Budget/Founder-Block)
        </div>
      ` : nothing}

      ${ov.pausedStrategies.length > 0 ? html`
        <div class="cp-monitoring__section">
          <p class="cp-monitoring__section-label">Pausierte Strategien (${ov.pausedStrategies.length})</p>
          <div class="cp-monitoring__table-wrap">
            <table class="cp-monitoring__table">
              <thead>
                <tr>
                  <th>Firma</th><th>Agent</th><th>Ziel</th><th>Grund</th><th>Seit</th><th></th>
                </tr>
              </thead>
              <tbody>
                ${ov.pausedStrategies.map(s => html`
                  <tr>
                    <td>${s.company_name}</td>
                    <td>${s.agent_name}</td>
                    <td>${s.goal_title}</td>
                    <td class="cp-monitoring__pause-reason">${s.block_reason ?? '—'}</td>
                    <td>${this._formatTime(s.updated_at)}</td>
                    <td>
                      <button
                        class="cp-monitoring__resume-btn"
                        ?disabled=${this._resuming.has(s.id)}
                        @click=${() => this._resumeStrategy(s.id)}
                      >
                        ${this._resuming.has(s.id) ? 'Wird fortgesetzt...' : 'Fortsetzen'}
                      </button>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>
      ` : nothing}

      <div class="cp-monitoring__section">
        <p class="cp-monitoring__section-label">Routinen-Status</p>
        <div class="cp-monitoring__table-wrap">
          <table class="cp-monitoring__table">
            <thead>
              <tr>
                <th>Name</th><th>Status</th><th>Letzter Lauf</th><th>Ergebnis</th><th>Provider</th><th>Modell</th>
              </tr>
            </thead>
            <tbody>
              ${ov.routines.map(r => html`
                <tr class="${r.enabled ? '' : 'cp-monitoring__row--disabled'}">
                  <td>${r.name}${!r.enabled ? html` <span class="cp-monitoring__disabled-tag">deaktiviert</span>` : nothing}</td>
                  <td>${r.enabled ? html`<span class="cp-monitoring__dot cp-monitoring__dot--on"></span>` : html`<span class="cp-monitoring__dot cp-monitoring__dot--off"></span>`}</td>
                  <td>${this._formatTime(r.last_exec_started_at ?? r.last_run)}</td>
                  <td>${this._statusBadge(r.last_exec_status)}</td>
                  <td>${r.provider ?? 'anthropic'}</td>
                  <td>${r.model ?? '—'}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>

        <div class="cp-monitoring__section">
          <h2>Memory-Audit</h2>
          <cp-memory-timeline></cp-memory-timeline>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-monitoring': CpPageMonitoring;
  }
}
